import { Star, Quote } from 'lucide-react'
import { Section } from '@/components/layout/Section'
import { Container } from '@/components/layout/Container'
import { CardSlider } from '@/components/ui/CardSlider'
import { AnimateIn } from '@/components/motion/AnimateIn'
import type { Testimonial } from '@/types/home'

const AVATAR_COLORS = [
  'bg-primary-100 text-primary-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-800',
  'bg-rose-100 text-rose-700',
  'bg-orange-100 text-orange-800',
  'bg-cyan-100 text-cyan-800',
]

export function Testimonials({ testimonials }: { testimonials: Testimonial[] }) {
  return (
    <div className="relative overflow-hidden bg-amber-50/40">
      <div
        className="pointer-events-none absolute -right-16 top-8 h-40 w-40 rounded-full bg-amber-100/60 blur-2xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-10 bottom-0 h-32 w-32 rounded-full bg-primary-100/40 blur-2xl"
        aria-hidden
      />

      <Container className="relative">
        <AnimateIn>
          <Section
            title="What Our Customers Say"
            subtitle="Real stories from buyers and sellers"
          >
            <CardSlider size="testimonial" navVariant="side" hideScrollbar>
              {testimonials.map((t, i) => (
                <TestimonialCard key={t.id} testimonial={t} colorIndex={i} />
              ))}
            </CardSlider>
          </Section>
        </AnimateIn>
      </Container>
    </div>
  )
}

function TestimonialCard({
  testimonial: t,
  colorIndex,
}: {
  testimonial: Testimonial
  colorIndex: number
}) {
  const initials = t.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)

  const avatarColor = AVATAR_COLORS[colorIndex % AVATAR_COLORS.length]

  return (
    <div className="flex h-full min-h-[210px] flex-col rounded-r-2xl rounded-l-md border-l-4 border-l-amber-400 bg-white p-4 shadow-[0_4px_20px_rgba(15,93,154,0.08)] sm:p-5">
      <Quote className="h-8 w-8 text-amber-300" aria-hidden />
      <p className="mt-2 flex-1 text-sm italic leading-relaxed text-neutral-600 line-clamp-5">
        &ldquo;{t.content}&rdquo;
      </p>
      <div className="mt-4 flex items-center gap-3 rounded-xl bg-neutral-50 px-3 py-3">
        {t.avatar ? (
          <img
            src={t.avatar}
            alt=""
            className="h-10 w-10 shrink-0 rounded-full object-cover"
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold ${avatarColor}`}
          >
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-neutral-900">{t.name}</p>
          <p className="truncate text-xs text-neutral-500">{t.role}</p>
          <div className="mt-1 flex gap-0.5">
            {Array.from({ length: t.rating }).map((_, i) => (
              <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
